import {
  Armchair,
  Coffee,
  Droplet,
  Building2,
  Plug,
  Sparkles,
  Package,
  UtensilsCrossed,
  MoreHorizontal,
  Banknote,
  CreditCard,
  Landmark,
  ReceiptText,
} from 'lucide-react'
import type {
  ExpenseCategory,
  PaymentMethod,
  PredefinedExpenseCategory,
} from '@/features/expenses/types/expense.types'
import { PREDEFINED_EXPENSE_CATEGORIES } from '@/features/expenses/types/expense.types'
import { formatCurrency } from '@/lib/formatters'

interface CategoryMeta {
  label: string
  icon: React.ComponentType<{ className?: string }>
}

/**
 * Categorical chart slots, in fixed order. Sourced from the validated data-viz
 * reference palette: eight hues whose ORDER is the colour-blind-safety
 * mechanism (worst adjacent CVD deltaE 9.1 light / 8.4 dark). Slots are assigned
 * in sequence and never cycled — a 9th series folds into the neutral "Other".
 */
export const CHART_SERIES_COLORS = [
  { light: '#2a78d6', dark: '#3987e5' }, // 1 blue
  { light: '#eb6834', dark: '#d95926' }, // 2 orange
  { light: '#1baf7a', dark: '#199e70' }, // 3 aqua
  { light: '#eda100', dark: '#c98500' }, // 4 yellow
  { light: '#e87ba4', dark: '#d55181' }, // 5 magenta
  { light: '#008300', dark: '#008300' }, // 6 green
  { light: '#4a3aa7', dark: '#9085e9' }, // 7 violet
  { light: '#e34948', dark: '#e66767' }, // 8 red
] as const

/** Neutral used for the "Other" bucket and any overflow beyond the eight slots. */
export const CHART_NEUTRAL_COLOR = { light: '#898781', dark: '#898781' } as const

const PREDEFINED_CATEGORY_META: Record<
  PredefinedExpenseCategory,
  CategoryMeta & { slot: number | null }
> = {
  furniture: { label: 'Furniture', icon: Armchair, slot: 6 },
  tea_coffee: { label: 'Tea & Coffee', icon: Coffee, slot: 1 },
  water: { label: 'Water', icon: Droplet, slot: 0 },
  rent: { label: 'Rent', icon: Building2, slot: 7 },
  utilities: { label: 'Utilities', icon: Plug, slot: 3 },
  cleaning_maintenance: { label: 'Cleaning & Maintenance', icon: Sparkles, slot: 2 },
  office_supplies: { label: 'Office Supplies', icon: Package, slot: 4 },
  meals_entertainment: { label: 'Meals & Entertainment', icon: UtensilsCrossed, slot: 5 },
  other: { label: 'Other', icon: MoreHorizontal, slot: null },
}

function isPredefined(category: string): category is PredefinedExpenseCategory {
  return (PREDEFINED_EXPENSE_CATEGORIES as readonly string[]).includes(category)
}

/** Title-cases a custom category slug/name for display. */
function humanizeCustom(category: string): string {
  return category
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

export function expenseCategoryLabel(category: ExpenseCategory): string {
  if (isPredefined(category)) return PREDEFINED_CATEGORY_META[category].label
  return humanizeCustom(category)
}

export function expenseCategoryIcon(
  category: ExpenseCategory,
): React.ComponentType<{ className?: string }> {
  if (isPredefined(category)) return PREDEFINED_CATEGORY_META[category].icon
  return ReceiptText
}

/**
 * Resolves a category to a chart colour for the given mode.
 *
 * Predefined categories hold a stable slot so a colour always means the same
 * category. Custom categories take the remaining slots in the order supplied
 * (`ordinal`), which keeps colour tied to the entity rather than to its rank —
 * a filter that drops series must not repaint the survivors, so pass the
 * ordinal from a stable list, not from a sorted-by-value index.
 */
export function expenseCategoryColor(
  category: ExpenseCategory,
  mode: 'light' | 'dark' = 'light',
  ordinal = 0,
): string {
  if (isPredefined(category)) {
    const { slot } = PREDEFINED_CATEGORY_META[category]
    if (slot == null) return CHART_NEUTRAL_COLOR[mode]
    return CHART_SERIES_COLORS[slot][mode]
  }
  if (ordinal >= CHART_SERIES_COLORS.length) return CHART_NEUTRAL_COLOR[mode]
  return CHART_SERIES_COLORS[ordinal][mode]
}

/**
 * Colours for every category in view. Predefined categories keep their fixed
 * slot; custom ones take the slots no predefined category in view is using,
 * in name order, so two categories on screen never share a colour. Past the
 * eighth they fold into the neutral, as the palette has no ninth hue.
 */
export function categoryColorMap(
  categories: ExpenseCategory[],
  mode: 'light' | 'dark' = 'light',
): Map<ExpenseCategory, string> {
  const colors = new Map<ExpenseCategory, string>()
  const taken = new Set<number>()
  for (const category of categories) {
    if (!isPredefined(category)) continue
    const { slot } = PREDEFINED_CATEGORY_META[category]
    if (slot == null) colors.set(category, CHART_NEUTRAL_COLOR[mode])
    else {
      taken.add(slot)
      colors.set(category, CHART_SERIES_COLORS[slot][mode])
    }
  }
  const free = CHART_SERIES_COLORS.map((_, slot) => slot).filter((slot) => !taken.has(slot))
  const custom = categories.filter((c) => !isPredefined(c)).sort((a, b) => a.localeCompare(b))
  custom.forEach((category, index) => {
    const slot = free[index]
    colors.set(category, slot == null ? CHART_NEUTRAL_COLOR[mode] : CHART_SERIES_COLORS[slot][mode])
  })
  return colors
}

const PAYMENT_METHOD_META: Record<
  PaymentMethod,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  cash: { label: 'Cash', icon: Banknote },
  card: { label: 'Card', icon: CreditCard },
  bank_transfer: { label: 'Bank Transfer', icon: Landmark },
  cheque: { label: 'Cheque', icon: ReceiptText },
  other: { label: 'Other', icon: MoreHorizontal },
}

export function paymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_METHOD_META[method]?.label ?? 'Other'
}

export function paymentMethodIcon(
  method: PaymentMethod,
): React.ComponentType<{ className?: string }> {
  return PAYMENT_METHOD_META[method]?.icon ?? MoreHorizontal
}

export function formatExpenseAmount(
  amount: number,
  currency?: string | null,
): string {
  return formatCurrency(amount, currency)
}

/** Pads a number to 2 digits. */
function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Formats a Date as YYYY-MM-DD (local). */
export function toDateString(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/**
 * Derives a { dateFrom, dateTo } range for a period mode relative to an anchor date.
 * - week: Monday–Sunday containing the anchor
 * - month: 1st–last day of the anchor's month
 * - year: Jan 1–Dec 31 of the anchor's year
 */
export function deriveDateRange(
  mode: 'week' | 'month' | 'year',
  anchorISO: string,
): { dateFrom: string; dateTo: string } {
  const anchor = new Date(anchorISO)
  const base = Number.isNaN(anchor.getTime()) ? new Date() : anchor
  if (mode === 'week') {
    const day = base.getDay() // 0 = Sun
    const diffToMonday = (day + 6) % 7
    const from = new Date(base)
    from.setDate(base.getDate() - diffToMonday)
    const to = new Date(from)
    to.setDate(from.getDate() + 6)
    return { dateFrom: toDateString(from), dateTo: toDateString(to) }
  }
  if (mode === 'month') {
    const from = new Date(base.getFullYear(), base.getMonth(), 1)
    const to = new Date(base.getFullYear(), base.getMonth() + 1, 0)
    return { dateFrom: toDateString(from), dateTo: toDateString(to) }
  }
  const from = new Date(base.getFullYear(), 0, 1)
  const to = new Date(base.getFullYear(), 11, 31)
  return { dateFrom: toDateString(from), dateTo: toDateString(to) }
}

export function formatExpenseDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
